<template>
  <div class="page-contact-directory" ref="topAnchor">
    <div v-if="firstVisit" class="page-contact-directory__search-container">
      <div class="page-contact-directory__welcome-content">
        <SmallLogo class="explore__smiley" />
        <div class="page-contact-directory__welcome-text">
          <div class="page-contact-directory__welcome-message">{{ $t('page.contact-directory.welcome-message') }}</div>
          <div class="page-contact-directory__welcome-message--subtitle">
            {{ $t('page.contact-directory.welcome-message-subtitle') }}
          </div>
        </div>
        <ContactDirectorySearchBar @search="search" />
      </div>
    </div>
    <div v-else>
      <BasePullToRefresh v-if="isTouchScreen" :pending="pending" @refresh="search(1)" />
      <ContactDirectoryHeader :with-search="!isPhone" @search="search" />
      <div class="page-contact-directory__content">
        <div v-if="isPhone" class="page-contact-directory__search-panel">
          <ContactDirectorySearchBar @search="search" />
        </div>
        <template v-if="hasResults">
          <span class="page-contact-directory__count">
            <!-- One inline box: on phones the line is a flex box, which drops a space between two items. -->
            <span>
              <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
              {{ ' ' + $t('page.contact-directory.record') }}
            </span>
          </span>
          <ContactsPhoneList
            v-if="isPhone"
            :results="phoneResults"
            :has-more="page < totalPages"
            :loading-more="loadingMore"
            :load-more-error="loadMoreError"
            :pending="pending"
            @load-more="loadMore"
          />
          <TableNormal
            v-else
            class="page-contact-directory__table"
            v-model="selectedContacts"
            :fixed-header="true"
            :headers="headers"
            :items="searchResults"
            :items-per-page="itemsPerPage"
            :page.sync="currentPage"
            hide-default-footer
            item-key="index"
            :mobile-breakpoint="1200"
          >
            <template v-slot:item.name="{ item }">
              <div class="page-contact-directory__row">
                <div class="page-contact-directory__row-title">
                  <div v-if="item.organization">
                    <BaseOrganizationCard :organization="item.organization" />
                  </div>
                  <div v-if="item.contactPoint">
                    <BaseContactCard :contact="item.contactPoint" />
                  </div>
                </div>
                <div v-if="item.type === 'ACCOUNT'" class="page-contact-directory__contacts">
                  <div v-for="(contactPoint, index) in item.organization.contactPoints" :key="index">
                    <BaseContactCard :contact="contactPoint" isSecondary />
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:item.score="{ item }">
              <BaseTag
                preset="soft"
                :tone="getContactDirectoryScoreTone(item.score)"
                v-tooltip="{ content: $t(`page.contact-directory.score.${getScoreClass(item.score)}`) }"
              >
                {{ item.score == null ? '' : item.score.toFixed(0) }}
              </BaseTag>
            </template>
          </TableNormal>
          <div v-if="totalPages > 1 && !isPhone" class="tenders-data-table__pagination">
            <TablePagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="isMobile ? 6 : 10"
              @input="onInputPage"
            />
          </div>
        </template>

        <TableState
          v-if="!hasResults"
          :state-loading="!hasResults && pending"
          :state-no-data="!hasResults && !pending"
          :state-text="pending ? $t('page.contact-directory.loading') : $t('page.contact-directory.no-result')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseOrganizationCard from '@/components/page-contact-directory/BaseOrganizationCard';
import BaseContactCard from '@/components/page-contact-directory/BaseContactCard';
import BasePullToRefresh from '@/components/base/BasePullToRefresh.vue';
import ContactsPhoneList from '@/components/page-contact-directory/ContactsPhoneList.vue';
import ContactDirectorySearchBar from '@/components/page-contact-directory/ContactDirectorySearchBar';
import ContactDirectoryHeader from '@/components/page-contact-directory/ContactDirectoryHeader';
import TableNormal from '@/components/table/TableNormal.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import TableState from '@/components/table/TableState.vue';
import SmallLogo from '@/assets/images/aloha-logomark.svg';
import BaseTag from '@/components/base/BaseTag.vue';
import { getContactDirectoryScoreTone } from '@/util/tag-tones/contact-directory-score-tag';
import { formatLocalizedNumber } from '@/util/number-util';

import IsMobileMixin from '@/mixins/is-mobile-mixin';
import contactDirectoryScoreMixin from '@/mixins/contact-directory-score-mixin';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PageContactDirectory',
  mixins: [IsMobileMixin, contactDirectoryScoreMixin],
  components: {
    BaseOrganizationCard,
    BaseContactCard,
    BasePullToRefresh,
    ContactsPhoneList,
    ContactDirectorySearchBar,
    ContactDirectoryHeader,
    TableNormal,
    TablePagination,
    TableState,
    SmallLogo,
    BaseTag,
  },
  data() {
    return {
      value: '',
      selectedContacts: [],
      itemsPerPage: 25,
      currentPage: null,
      firstVisit: true, // Add firstVisit data property
    };
  },
  computed: {
    ...mapGetters('contactDirectory', [
      'searchResults',
      'page',
      'totalPages',
      'totalCount',
      'pending',
      'loadingMore',
      'loadMoreError',
    ]),
    isPhone() {
      return viewport.size === 'phone';
    },
    isTouchScreen() {
      return !viewport.canHover;
    },
    // The results carry no id of their own (the same person can come back twice): a page
    // added below keeps the earlier positions, so a position names a row.
    phoneResults() {
      return this.searchResults.map((result, index) => ({ ...result, key: index }));
    },
    ...mapGetters('contactDirectorySearchFilters', ['searchFilters']),
    headers() {
      return [
        {
          text: this.$t('page.admin-users.name'),
          value: 'name',
        },
        {
          text: 'Score',
          value: 'score',
          width: '5%',
        },
      ];
    },
    hasResults() {
      return this.searchResults.length > 0;
    },
  },
  methods: {
    ...mapActions('contactDirectory', ['searchContactDirectory', 'loadMoreContactDirectory']),
    ...mapActions('contactDirectorySearchFilters', ['resetContactDirectoryKeywords']),
    getContactDirectoryScoreTone,
    formatLocalizedNumber,
    search(page = 1, pageSize = this.itemsPerPage) {
      this.firstVisit = false;
      this.searchContactDirectory({ keywords: [`${this.searchFilters.keywords}`], page, pageSize });
    },
    onInputPage(pageNumber) {
      this.search(pageNumber);
      this.$refs.topAnchor.scrollIntoView({ block: 'start', behavior: 'smooth', inline: 'start' });
    },
    loadMore() {
      // Asking for a page now would race the search that's running.
      if (this.pending || this.page >= this.totalPages) return;
      this.loadMoreContactDirectory({
        keywords: [`${this.searchFilters.keywords}`],
        page: this.page + 1,
        pageSize: this.itemsPerPage,
      });
    },
  },
  created() {
    this.resetContactDirectoryKeywords();
  },
  mounted() {
    this.currentPage = this.page;
  },
  watch: {
    page() {
      this.currentPage = this.page;
    },
    // Crossing the phone width (a rotation, a resized window): the phone list grows from page
    // 1 and the table shows one page, so search again for what the new layout expects.
    isPhone(isPhone) {
      if (this.firstVisit) return;
      const pagesListed = this.searchResults.length > this.itemsPerPage;
      if ((isPhone && this.page > 1) || (!isPhone && pagesListed)) this.search(1);
    },
  },
};
</script>

<style lang="scss">
.page-contact-directory {
  &__search-container {
    background: $color-white;
    display: flex;
    height: calc(var(--viewport-height) - 56px);
  }

  &__welcome-content {
    min-width: 600px;
    display: block;
    margin: auto;

    .explore__smiley {
      width: 140px;
      height: 140px;
      display: block;
      margin: 0 auto 64px;
    }

    input {
      display: flex;
      margin: auto;
    }
  }

  &__welcome-text {
    margin-bottom: 32px;
  }

  &__welcome-message {
    text-align: center;
    font-size: 24px;
    color: $color-normal;
    padding-bottom: 4px;

    &--subtitle {
      text-align: center;
      font-size: 16px;
      color: $color-normal-pale;
    }
  }

  &__table {
    &.v-data-table {
      tbody {
        cursor: default !important;
      }

      .v-data-table__mobile-row {
        width: 100%;
      }
    }
  }

  &__content {
    padding: 32px;
    display: flex;
    flex-direction: column;

    @include screen-below('tablet') {
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
    margin-bottom: 8px;

    // Phones: the other lists' count line, its buttons' height and as far from the list, though
    // this one has no buttons.
    @include screen-below('tablet') {
      display: flex;
      align-items: center;
      min-height: 36px;
      margin-bottom: 12px;
    }
  }

  // Phones: under the header, as the other lists' search and filters, and as tall as theirs.
  &__search-panel {
    position: relative;
    z-index: 2;
    margin: -32px 0 16px;
    background: $color-white;
    box-shadow: $shadow-dropdown;

    @include phone-filter-panel;

    .contact-directory-search-bar {
      width: 100%;
      min-height: 40px;
      margin: 0;
      padding: 0 8px 0 12px;

      .base-button__element {
        min-height: 32px;
      }
    }
  }

  &__contacts {
    display: flex;
    gap: 16px;
  }

  &__row {
    display: flex;
    gap: 16px;
    color: $color-normal;
    align-items: center;
    flex-wrap: wrap;

    &--infos {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }

  &__row-title {
    min-width: 30%;
  }

  @media screen and (max-width: 784px) {
    &__welcome-content {
      width: calc(100% - 56px);
      min-width: 300px;
    }
  }
}
</style>
